import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";

import { Middleware } from "@reduxjs/toolkit";

export const localStorageMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);
    const state = store.getState();
    localStorage.setItem(
      "bookAppointmentState",
      JSON.stringify(state.bookAppointment)
    );
    return result;
  };

// const secretKey = process.env.REACT_APP_SECRET_KEY as string;
export const secretKey = "mysecret";

if (!secretKey) {
  throw new Error(
    "Secret key is not defined. Please set REACT_APP_SECRET_KEY in your environment variables."
  );
}

const checkTokenExpiry = (tokenStr?: string): boolean => {
  let expireTime: number = 0;
  if (expireTime === 0) {
    try {
      const token = tokenStr || getTokenFromLocalStore("access_token");
      if (token) {
        const decodedToken = jwtDecode<{ exp: number }>(token);
        expireTime = decodedToken.exp;
      }
    } catch (error) {
      console.error("Token decoding failed:", error);
      return true;
    }
  }
  return Date.now() >= expireTime * 1000;
};

export const encryptData = (data: any, secretKey: string): string => {
  const ciphertext = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    secretKey
  ).toString();
  return ciphertext;
};

export const decryptData = (ciphertext: string, secretKey: string): any => {
  try {
    if (!ciphertext) {
      throw new Error("No ciphertext provided for decryption");
    }
    const bytes = CryptoJS.AES.decrypt(ciphertext, secretKey);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedData) {
      throw new Error("Failed to decrypt data or data is empty");
    }
    return JSON.parse(decryptedData);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null; // Return null if decryption fails
  }
};

const getTokenFromLocalStore = <T>(key: string): T | null => {
  try {
    const encryptedData = sessionStorage.getItem(key);
    if (!encryptedData) {
      console.warn(`No data found in sessionStorage for key: ${key}`);
      return null;
    }
    return decryptData(encryptedData, secretKey) as T;
  } catch (error) {
    console.error("Error retrieving token from local storage:", error);
    return null;
  }
};

const saveTokenToLocalStore = (key: string, value: any) => {
  const encryptedData = encryptData(value, secretKey);
  sessionStorage.setItem(key, encryptedData);
};

const saveKeyToLocalStore = (key: string, value: string) => {
  const encryptedData = encryptData(value, secretKey);
  sessionStorage.setItem(key, encryptedData);
};

const clearAll = (changeWindow: boolean = true) => {
  sessionStorage.clear();
  if (changeWindow && typeof window !== "undefined") {
    window.location.href = "/auth";
  }
};

const clearKey = (specificKey: string) => {
  sessionStorage.removeItem(specificKey);
};

interface IBookingSummary {
  serviceType: string;
  serviceOffering: string;
  offeringOption: string;
  appointmentDate: string;
  appointmentTime: string;
  numberOfPeople: number;
  location: string;
}

interface IAllBookingSummary {
  booking_summary: IBookingSummary[];
}

export const getBookingSummary = (): IAllBookingSummary | null => {
  try {
    const encrypted = sessionStorage.getItem("booking_summary");
    if (!encrypted) return null;
    return decryptData(encrypted, secretKey) as IAllBookingSummary;
  } catch (err) {
    console.error("Failed to get booking summary:", err);
    return null;
  }
};

// booking summary
export const updateBookingSummary = (newData: Partial<any>): void => {
  const existingData = getBookingSummary() || { booking_summary: [] };
  const merged = {
    ...existingData,
    ...newData,
    booking_summary: [...existingData.booking_summary, newData],
  };

  saveTokenToLocalStore("booking_summary", merged);
};

export {
  getTokenFromLocalStore,
  saveTokenToLocalStore,
  checkTokenExpiry,
  clearAll,
  clearKey,
  saveKeyToLocalStore,
};
